import { defineStore } from 'pinia';
import { router } from '@/router';
import { fetchWrapper } from '@/utils/helpers/fetch-wrapper';
import { useProfileStore } from '@/stores/profile';
import { FirebaseAuthService } from '@/firebase/firebase-auth-service';
import { authSyncService } from '@/services/auth-sync.service';

function decodeJwtSub(token: string): string | null {
  try {
    const payload = token.split('.')[1];
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(padded)).sub ?? null;
  } catch {
    return null;
  }
}

const authUrl = `${import.meta.env.VITE_API_URL}/v3/auth`;
const companyRegistrationUrl = `${import.meta.env.VITE_API_URL}/v3/company-registration`;

export interface UserLoginDto {
  email: string;
  password: string;
}
export interface CreateUserDto {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  name: string;
  website: string;
  industry: string;
  employees: number;
  consent: boolean;
}

interface JwtToken {
  accessToken: string;
  refreshToken: string;
}
interface InviteLinkResponse {
  data: {
    code: string;
  };
}

interface CreateInviteDto {
  teamId: string;
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    jwt: getJwtFromStorage(),
    returnUrl: null as string | null,
    errorCode: null as string | null,
  }),
  getters: {
    userId: (state): string | null => (state.jwt?.accessToken ? decodeJwtSub(state.jwt.accessToken) : null),
  },
  actions: {
    async login(userData: UserLoginDto, options: { keepSignedIn: boolean }): Promise<void> {
      try {
        const jwt = await fetchWrapper.post(`${authUrl}/login`, userData);
        this.jwt = jwt;

        if (options.keepSignedIn) {
          localStorage.setItem('jwt', JSON.stringify(jwt));
          sessionStorage.removeItem('jwt');
        } else {
          sessionStorage.setItem('jwt', JSON.stringify(jwt));
          localStorage.removeItem('jwt');
        }

        authSyncService.broadcastLogin(jwt);

        fetchWrapper
          .post(`${authUrl}/firebase-token`, {})
          .then((firebaseToken) => {
            if (firebaseToken?.token) {
              FirebaseAuthService.signInWithToken(firebaseToken.token);
            } else {
              console.warn('No Firebase token received');
            }
          })
          .catch((firebaseError) => {
            console.warn('Firebase authentication failed:', firebaseError);
          });

        const profileStore = useProfileStore();
        await profileStore.fetchProfile();

        router.push(this.returnUrl || '/profile/edit');
      } catch (error: any) {
        this.errorCode = error?.code ?? 'generic_error';
        throw { ...error, code: this.errorCode };
      }
    },

    handleExternalLogin(jwt: JwtToken) {
      this.jwt = jwt;

      persistJwt(jwt);

      const profileStore = useProfileStore();

      profileStore
        .fetchProfile()
        .then(() => {
          if (router.currentRoute.value.path !== '/auth/login') {
            router.push('/profile/edit');
          }
        })
        .catch((error) => {
          console.error('Failed to fetch profile after external login:', error);
        });
    },

    logout() {
      clearAuthState();
      this.jwt = null;
      authSyncService.broadcastLogout();
      router.push('/auth/login');
    },

    handleExternalLogout() {
      clearAuthState();
      this.jwt = null;
      if (router.currentRoute.value.path !== '/auth/login') {
        router.push('/auth/login');
      }
    },

    async registerNewCompany(userData: CreateUserDto) {
      try {
        await fetchWrapper.post(companyRegistrationUrl, userData);
        router.push(this.returnUrl || '/auth/login');
      } catch (error: any) {
        this.errorCode = error?.code ?? 'generic_error';
        throw { ...error, code: this.errorCode };
      }
    },

    updateTokens(refreshedJwt: JwtToken) {
      this.jwt = refreshedJwt;
      persistJwt(refreshedJwt);
      authSyncService.broadcastTokenRefresh(refreshedJwt);
    },

    handleExternalTokenRefresh(jwt: JwtToken) {
      this.jwt = jwt;
      persistJwt(jwt);
    },

    async forgotPassword(email: string): Promise<void> {
      try {
        await fetchWrapper.post(`${authUrl}/forgot-password`, {
          email,
        });
      } catch (error: any) {
        this.errorCode = error?.code ?? 'generic_error';
        throw { ...error, code: this.errorCode };
      }
    },

    async changePassword(data: { email: string; code: string; newPassword: string }): Promise<void> {
      try {
        await fetchWrapper.post(`${authUrl}/change-password`, data);
      } catch (error: any) {
        this.errorCode = error?.code ?? 'generic_error';
        throw { ...error, code: this.errorCode };
      }
    },

    async createInvite(inviteData: CreateInviteDto): Promise<string> {
      try {
        const response: InviteLinkResponse = await fetchWrapper.post(`${authUrl}/create-invite`, inviteData);
        return response.data.code;
      } catch (error: any) {
        this.errorCode = error?.code ?? 'generic_error';
        throw { ...error, code: this.errorCode };
      }
    },

    async createPsychoCompanyInvite(): Promise<string> {
      try {
        const response: InviteLinkResponse = await fetchWrapper.post(`${authUrl}/create-psycho-company-invite`, {});
        return response.data.code;
      } catch (error: any) {
        this.errorCode = error?.code ?? 'generic_error';
        throw { ...error, code: this.errorCode };
      }
    },
  },
});

function getJwtFromStorage(): JwtToken | null {
  try {
    const localJwt = localStorage.getItem('jwt');
    if (localJwt) {
      return JSON.parse(localJwt);
    }

    const sessionJwt = sessionStorage.getItem('jwt');
    if (sessionJwt) {
      return JSON.parse(sessionJwt);
    }

    return null;
  } catch (error) {
    console.error('Error parsing JWT from storage:', error);
    return null;
  }
}

function persistJwt(jwt: JwtToken) {
  const wasInLocalStorage = localStorage.getItem('jwt') !== null;

  if (wasInLocalStorage) {
    localStorage.setItem('jwt', JSON.stringify(jwt));
    sessionStorage.removeItem('jwt');
  } else {
    sessionStorage.setItem('jwt', JSON.stringify(jwt));
    localStorage.removeItem('jwt');
  }
}

function clearAuthState() {
  FirebaseAuthService.signOut();
  localStorage.removeItem('jwt');
  sessionStorage.removeItem('jwt');

  const profileStore = useProfileStore();
  profileStore.profile = null;
  profileStore.clearRole();
}
